import type { ComponentPropsWithoutRef } from "react";
import { Plus } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * The control that adds something, always led by its plus.
 *
 * It is not one of `Button`'s five jobs. Mostly it is a place in a list where
 * the next item would go, which is why it spans the list and is drawn as a
 * dashed well rather than as a control beside one. Three screens drew their own
 * at three weights of dash and three tints. The design has two smaller forms of
 * it: beside something the page offers, and for something of the attorney's own.
 */
const WELL =
  "flex w-full justify-center border-[1.5px] border-dashed border-border font-bold";

const EMPHASIS = {
  /** Adding something subordinate, under what it belongs to: a dependent claim. */
  neutral: cn(
    WELL,
    "rounded-inset gap-1.5 bg-surface-sunken p-[9px] text-[11.5px] text-muted-foreground hover:bg-muted hover:text-primary-soft-foreground [&_svg]:size-[13px]",
  ),
  /** Adding the thing the section is for: an independent claim, an element. */
  brand: cn(
    WELL,
    "rounded-[11px] gap-[7px] bg-muted p-[11px] text-[12px] text-primary-soft-foreground hover:border-primary hover:bg-muted-hover [&_svg]:size-3.5",
  ),
  /** Adding an item the page offers, beside its description: a claim form from the catalogue. */
  offer:
    "inline-flex h-[26px] flex-none gap-[5px] rounded-sm border border-border bg-muted px-[11px] text-[12px] font-bold text-brand-mid hover:bg-muted-hover [&_svg]:size-[13px]",
  /** Starting one of the attorney's own, under what the page offers: a claim category. */
  own: "inline-flex gap-[7px] rounded-inset border border-border bg-card px-[13px] py-2 text-detail font-semibold text-primary hover:bg-muted [&_svg]:size-3.5",
} as const;

export function AddButton({
  emphasis = "neutral",
  className,
  children,
  ...rest
}: Omit<ComponentPropsWithoutRef<"button">, "type"> & {
  emphasis?: keyof typeof EMPHASIS;
}) {
  return (
    <button
      type="button"
      className={cn(
        "cursor-pointer items-center focus-visible:ring-2 focus-visible:ring-ring/50 focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50",
        EMPHASIS[emphasis],
        className,
      )}
      {...rest}
    >
      <Plus aria-hidden="true" />
      {children}
    </button>
  );
}
